// Single source of truth for site-wide configuration.
// Everything that depends on the production domain or page metadata imports from here.

export const SITE_URL = 'https://ryens.com'
export const SITE_NAME = 'Ryens'
export const SITE_TAGLINE = 'Senior software studio · AI, mobile, web & SaaS'
export const SITE_DESCRIPTION =
  'Ryens is a senior software studio from Dhaka building AI, mobile, web and SaaS products for funded teams worldwide. MVPs shipped in ~6 weeks.'

export const SOCIAL = {
  linkedin: {
    handle: 'ryens',
    url: 'https://www.linkedin.com/company/ryens',
  },
  github: {
    handle: 'ryens',
    url: 'https://github.com/ryens',
  },
  x: {
    handle: 'ryens',
    url: 'https://x.com/ryens',
  },
}

export const ORG = {
  legalName: 'Ryens',
  city: 'Dhaka',
  country: 'BD',
  email: 'hello@ryens.com',
}

/**
 * Returns an absolute URL by joining SITE_URL and the given path.
 * - Collapses duplicate slashes (after the protocol)
 * - absoluteUrl('/') and absoluteUrl('') both return 'https://ryens.com' (no trailing slash)
 * - absoluteUrl('/about') and absoluteUrl('about') both return 'https://ryens.com/about'
 */
export function absoluteUrl(path: string): string {
  // Collapse any leading slashes to a single segment; root resolves to the bare origin.
  const clean = path.replace(/^\/+/, '')
  return clean ? `${SITE_URL}/${clean}` : SITE_URL
}

export type ChangeFrequency =
  | 'always'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'never'

export type PageEntry = {
  path: string
  title: string
  description: string
  priority: number
  changeFrequency: ChangeFrequency
}

export const PAGES: Record<string, PageEntry> = {
  home: {
    path: '/',
    title: '', // uses layout title template default
    description: SITE_DESCRIPTION,
    priority: 1.0,
    changeFrequency: 'monthly',
  },
  about: {
    path: '/about',
    title: 'About',
    description: 'A senior software studio from Dhaka building for the world. Offshore done right.',
    priority: 0.8,
    changeFrequency: 'monthly',
  },
  services: {
    path: '/services',
    title: 'Services',
    description: 'AI, mobile, web, SaaS and MVP development from one senior team. No handoffs, no seams.',
    priority: 0.9,
    changeFrequency: 'monthly',
  },
  mvp: {
    path: '/mvp',
    title: 'MVP',
    description: 'A real, production-ready MVP in about six weeks. Fixed scope, fixed price, AI-native, yours to keep.',
    priority: 0.9,
    changeFrequency: 'monthly',
  },
  work: {
    path: '/work',
    title: 'Work',
    description: 'Selected products Ryens took from roadmap to results across fintech, health, logistics and SaaS.',
    priority: 0.8,
    changeFrequency: 'weekly',
  },
  blog: {
    path: '/blog',
    title: 'Insights',
    description: 'Notes on building real AI, mobile, web and SaaS products, for founders and product teams.',
    priority: 0.8,
    changeFrequency: 'weekly',
  },
  careers: {
    path: '/careers',
    title: 'Careers',
    description: 'Join a small senior team shipping real products for clients worldwide.',
    priority: 0.7,
    changeFrequency: 'monthly',
  },
  contact: {
    path: '/contact',
    title: 'Contact',
    description: "Tell us what you're building. We reply within one business day.",
    priority: 0.9,
    changeFrequency: 'monthly',
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy',
    description: 'How Ryens collects, uses and protects your information.',
    priority: 0.3,
    changeFrequency: 'monthly',
  },
  terms: {
    path: '/terms',
    title: 'Terms',
    description: 'The terms that govern use of the Ryens website and services.',
    priority: 0.3,
    changeFrequency: 'monthly',
  },
}
